import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  BenutzerResponse,
  ChatAntwortstil,
  DiagnoseChatAntwortstil,
  ProfilSichtbarkeit,
} from '../models/benutzer.models';

/**
 * Self-Service für das eigene Profil des eingeloggten Users.
 *
 * Unterschied zum `BenutzerService`:
 *  - `BenutzerService` ist Admin-Sicht (Liste, Rollen, Membership-Felder).
 *  - `ProfileService` ist User-Sicht auf die eigene Identität inkl. Passwort.
 */
export interface UpdateProfileRequest {
  vorname: string;
  nachname: string;
  email: string;
  telefon?: string | null;
  mobil?: string | null;
  position?: string | null;
  abteilung?: string | null;
  unternehmen?: string | null;
  passwort?: string;
}

/**
 * Teilupdate-Body für `PUT /my/profile/chat-einstellungen`. Beide Felder
 * optional — es wird nur gesendet, was der User tatsächlich umgeschaltet hat.
 */
export interface UpdateChatEinstellungenRequest {
  antwortstil?: ChatAntwortstil;
  lageberichtAktiv?: boolean;
  /** Eigener Stil des Systemdiagnose-Chats (kennt zusätzlich 'ki_agent'). */
  diagnoseAntwortstil?: DiagnoseChatAntwortstil;
}

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly http = inject(HttpClient);

  get(): Observable<BenutzerResponse> {
    return this.http.get<BenutzerResponse>('/my/profile');
  }

  update(data: UpdateProfileRequest): Observable<BenutzerResponse> {
    return this.http.put<BenutzerResponse>('/my/profile', data);
  }

  /**
   * Setzt den Standard-Mandanten des Users. `mandantId === null` entfernt
   * einen vorhandenen Standard wieder.
   *
   * Beim nächsten Login (oder beim ersten Page-Load nach Logout) wird der
   * Standard-Mandant automatisch zum aktiven Mandanten — der User landet
   * direkt im gewünschten Kontext.
   */
  setDefaultMandant(mandantId: string | null): Observable<BenutzerResponse> {
    return this.http.put<BenutzerResponse>('/my/profile/default-mandant', { mandantId });
  }

  /**
   * Liefert die Avatar-URL für das aktuelle Profilbild des eingeloggten Users
   * inkl. Cache-Bust-Parameter (`?v=<updatedAt>`), damit der Browser nach
   * Upload/Delete sofort das neue Bild lädt. Null wenn der User kein Bild
   * gesetzt hat — Frontend zeigt dann den Initialen-Fallback.
   *
   * Hinweis: Der HTTP-Interceptor prefixed nur Pfade ohne http(s)-Schema mit
   * `environment.apiUrl`. Da diese URL direkt von einem `<img [src]>` aus
   * geladen wird (am Interceptor vorbei), bauen wir die volle URL hier auf.
   */
  getPictureUrl(updatedAt: string | null | undefined): string | null {
    if (!updatedAt) return null;
    const v = encodeURIComponent(updatedAt);
    return `${environment.apiUrl}/my/profile/picture?v=${v}`;
  }

  /**
   * Lädt ein neues Profilbild hoch (multipart/form-data). Backend akzeptiert
   * JPG/PNG/GIF, max 2 MB; antwortet mit 204 No Content. Der API-Interceptor
   * erkennt FormData-Bodies und überspringt die msgpack-Codierung — der
   * Browser setzt Content-Type inkl. boundary automatisch.
   */
  uploadPicture(file: File): Observable<void> {
    const fd = new FormData();
    fd.append('file', file);
    return this.http.put<void>('/my/profile/picture', fd);
  }

  /**
   * Entfernt das aktuell gesetzte Profilbild des Users.
   * Backend antwortet mit 204 No Content.
   */
  deletePicture(): Observable<void> {
    return this.http.delete<void>('/my/profile/picture');
  }

  /**
   * Liest die aktuellen Sichtbarkeitseinstellungen des Profils (Master-Schalter
   * `public`/`private` + 6 Pro-Feld-Flags).
   *
   * Endpoint: `GET /api/v1/profil/sichtbarkeit` — msgpack-codiert via
   * `apiInterceptor`. URL ohne `/api/v1`-Prefix, weil der Interceptor das
   * `environment.apiUrl` (`/api/v1`) automatisch voranstellt.
   */
  getSichtbarkeit(): Observable<ProfilSichtbarkeit> {
    return this.http.get<ProfilSichtbarkeit>('/profil/sichtbarkeit');
  }

  /**
   * Aktualisiert die Sichtbarkeitseinstellungen atomar (Master + alle Flags).
   * Backend gibt den neuen, kanonischen Stand zurueck.
   *
   * Endpoint: `PUT /api/v1/profil/sichtbarkeit` — msgpack-codiert via
   * `apiInterceptor`.
   */
  updateSichtbarkeit(payload: ProfilSichtbarkeit): Observable<ProfilSichtbarkeit> {
    return this.http.put<ProfilSichtbarkeit>('/profil/sichtbarkeit', payload);
  }

  /**
   * Aktualisiert die Chat-Einstellungen des KI-Projektleiter-Chats
   * (Antwortstil, Lagebericht-zum-Start) als Teilupdate. Backend antwortet
   * mit der vollen, aktualisierten Profil-Response.
   *
   * Endpoint: `PUT /my/profile/chat-einstellungen`.
   */
  updateChatEinstellungen(payload: UpdateChatEinstellungenRequest): Observable<BenutzerResponse> {
    return this.http.put<BenutzerResponse>('/my/profile/chat-einstellungen', payload);
  }
}
